// src/shared/constants/seecExceptionRules.js
//
// Scaffolding for SEEC filing exception detection.
// These rules are intended to map machine-detectable filing issues to a single
// law catalog entry and a concrete detection method.

export const SEEC_EXCEPTION_RULES = [
  {
    id: "missing_itemized_contributor_name",
    lawId: "SEEC-0105",
    title: "Missing contributor name on itemized receipt",
    category: "receipts",
    severity: "high",
    datasets: ["SEEC_RECEIPTS"],
    enabled: true,
    description:
      "Flags itemized contribution rows that appear to require a contributor identity but do not contain a contributor name.",
    detectionMethod: "deterministic",
  },
  {
    id: "missing_itemized_contributor_address",
    lawId: "SEEC-0105",
    title: "Missing contributor address on itemized receipt",
    category: "receipts",
    severity: "high",
    datasets: ["SEEC_RECEIPTS"],
    enabled: true,
    description:
      "Flags itemized contribution rows missing street or locality details when a contributor appears to be individually identified.",
    detectionMethod: "deterministic",
  },
  {
    id: "missing_receipt_occupation_or_employer",
    lawId: "SEEC-0105",
    title: "Missing occupation or employer",
    category: "receipts",
    severity: "medium",
    datasets: ["SEEC_RECEIPTS"],
    enabled: true,
    description:
      "Flags itemized contribution rows missing occupation or employer information where those fields are expected.",
    detectionMethod: "deterministic",
  },
  {
    id: "anonymous_contribution_handling",
    lawId: "SEEC-0106",
    title: "Potential anonymous contribution handling issue",
    category: "receipts",
    severity: "high",
    datasets: ["SEEC_RECEIPTS"],
    enabled: true,
    description:
      "Flags receipt patterns that may indicate anonymous or insufficiently identified contributions.",
    detectionMethod: "deterministic",
  },
  {
    id: "missing_expenditure_payee_or_purpose",
    lawId: "SEEC-0107",
    title: "Missing payee or expenditure purpose",
    category: "expenditures",
    severity: "high",
    datasets: ["SEEC_DISBURSEMENTS"],
    enabled: true,
    description:
      "Flags expenditure rows missing a payee, purpose of expenditure, or comparable identifying detail.",
    detectionMethod: "deterministic",
  },
  {
    id: "summary_balance_rollforward_mismatch",
    lawId: "SEEC-0108",
    title: "Summary balance rollforward mismatch",
    category: "disclosure_reporting",
    severity: "high",
    datasets: ["SEEC_SUMMARIES"],
    enabled: true,
    description:
      "Flags adjacent filings where the ending balance of one period does not match the beginning balance of the next period.",
    detectionMethod: "deterministic",
  },
  {
    id: "late_or_missing_electronic_filing_signal",
    lawId: "SEEC-0150",
    title: "Possible electronic filing issue",
    category: "disclosure_reporting",
    severity: "medium",
    datasets: ["SEEC_SUMMARIES"],
    enabled: true,
    description:
      "Flags records that may indicate missing, delayed, or inconsistent electronic filing behavior.",
    detectionMethod: "deterministic",
  },
  {
    id: "llm_narrative_review",
    lawId: null,
    title: "Narrative anomaly review",
    category: "review",
    severity: "medium",
    datasets: ["SEEC_RECEIPTS", "SEEC_DISBURSEMENTS", "SEEC_SUMMARIES"],
    enabled: false,
    description:
      "Reserved for optional OpenAI-generated narrative review after deterministic findings are assembled.",
    detectionMethod: "llm",
  },
];

export const SEEC_EXCEPTION_RULE_MAP = Object.fromEntries(
  SEEC_EXCEPTION_RULES.map((rule) => [rule.id, rule]),
);

export function getSeecExceptionRule(ruleId) {
  return SEEC_EXCEPTION_RULE_MAP[ruleId] || null;
}
